import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Phone,
  Gift,
  Home,
  QrCode,
  ChevronRight,
  LogOut,
  Settings,
  Star,
  Edit,
  ArrowLeft,
  Lock,
  MessageSquare,
  HelpCircle,
  Wallet,
  Shield,
  Bell
} from 'lucide-react';
import { loadClient, logoutClient } from '../Redux/Actions/ClientActions';
import './MyPoints.css'; // Global tokens
import './ScanPoints.css'; // Navigation layouts
import './ClientProfile.css';

const ClientProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { client, points, loading } = useSelector(state => state.clientReducer) || {};

  useEffect(() => {
    if (!client) {
      dispatch(loadClient());
    }
  }, [dispatch, client]);

  const handleLogout = () => {
    dispatch(logoutClient());
    navigate('/login-client');
  };

  const getTier = (pts) => {
    if (pts >= 5000) return 'Diamant';
    if (pts >= 2500) return 'Or';
    if (pts >= 1500) return 'Argent';
    return 'Bronze';
  };

  const getTierStyle = (tier) => {
    switch (tier) {
      case 'Diamant': return 'linear-gradient(135deg, #2A2F6B 0%, #1f2350 100%)'; // Bleu foncé premium
      case 'Or': return 'linear-gradient(135deg, #fce38a 0%, #f38181 100%)'; // Aspect Or chaud
      case 'Argent': return 'linear-gradient(135deg, #2A2F6B 0%, #3d4489 100%)'; // Bleu du logo
      default: return 'linear-gradient(135deg, #2A2F6B 0%, #1f2350 100%)'; // Bleu du logo pour Bronze
    }
  };

  const currentTier = getTier(points || 0);

  return (
    <div className="mobile-app-container">
      <header style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        padding: '1.25rem 1.5rem',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'transparent'
      }}>
        <button
          onClick={() => navigate('/my-points')}
          style={{
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)', // Plus subtil sur les fonds colorés
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            cursor: 'pointer',
            backdropFilter: 'blur(5px)',
            zIndex: 20
          }}
        >
          <ArrowLeft size={24} strokeWidth={2.5} />
        </button>
        <button
          style={{
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
            zIndex: 20
          }}
        >
          <Settings
            size={24}
            strokeWidth={2.5}
            style={{ minWidth: '24px', minHeight: '24px', display: 'block' }}
          />
        </button>
      </header>

      {/* Profile Hero */}
      <section className="profile-hero-section" style={{ background: getTierStyle(currentTier) }}>
        <div className="profile-avatar-container">
          <div className="profile-avatar" style={{ color: '#FF9933' }}>
            {client?.name?.charAt(0).toUpperCase() || 'C'}
          </div>
          <div className="avatar-badge" style={{ background: 'white', color: '#333' }}>
            <Shield size={16} fill="currentColor" />
          </div>
        </div>
        <h1 className="profile-name">{client?.name || 'Client'}</h1>

        <div className="flex flex-col items-center gap-1 mt-1">
          <p className="profile-email flex items-center gap-1.5">
            <Mail size={12} className="opacity-80" />
            {client?.email || 'email@exemple.com'}
          </p>
          {client?.phone && (
            <p className="profile-email flex items-center gap-1.5">
              <Phone size={12} className="opacity-80" />
              {client.phone}
            </p>
          )}
          {client?.createdAt && (
            <p className="text-xs text-white/80 mt-1">
              Membre depuis {new Date(client.createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>

        <div className="profile-stats-bar mt-4">
          <div className="profile-stat-pill">
            <Star size={12} className="inline mr-1 fill-white" />
            Niveau {currentTier}
          </div>
          <div className="profile-stat-pill">
            <Gift size={12} className="inline mr-1" />
            {points?.toLocaleString() || 0} Points
          </div>
        </div>
      </section>

      <main className="mobile-main-feed animate-fade-in">
        {/* Account Group */}
        <div className="settings-group">
          <h3 className="settings-group-title">Mon Compte</h3>
          <div className="settings-card">
            <div className="settings-item" onClick={() => navigate('/update-profil')}>
              <div className="settings-item-left">
                <div className="settings-icon-box" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                  <User size={18} />
                </div>
                <span className="settings-item-label">Détails du profil</span>
              </div>
              <ChevronRight size={18} className="text-slate-300" />
            </div>
            <div className="settings-item">
              <div className="settings-item-left">
                <div className="settings-icon-box" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <Bell size={18} />
                </div>
                <span className="settings-item-label">Notifications</span>
              </div>
              <span className="settings-item-value">Activées</span>
            </div>
            <div className="settings-item">
              <div className="settings-item-left">
                <div className="settings-icon-box" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                  <Lock size={18} />
                </div>
                <span className="settings-item-label">Sécurité</span>
              </div>
              <ChevronRight size={18} className="text-slate-300" />
            </div>
          </div>
        </div>

        {/* Support Group */}
        <div className="settings-group">
          <h3 className="settings-group-title">Support & Aide</h3>
          <div className="settings-card">
            <div className="settings-item" onClick={() => navigate('/help-center')}>
              <div className="settings-item-left">
                <div className="settings-icon-box" style={{ background: 'rgba(14, 165, 233, 0.1)', color: '#0ea5e9' }}>
                  <MessageSquare size={18} />
                </div>
                <span className="settings-item-label">Centre d'aide</span>
              </div>
              <ChevronRight size={18} className="text-slate-300" />
            </div>
            <div className="settings-item">
              <div className="settings-item-left">
                <div className="settings-icon-box" style={{ background: 'rgba(71, 85, 105, 0.1)', color: '#475569' }}>
                  <HelpCircle size={18} />
                </div>
                <span className="settings-item-label">À propos</span>
              </div>
              <span className="settings-item-value">v1.2.0</span>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button className="btn-logout-premium" onClick={handleLogout}>
          <LogOut size={20} />
          Déconnexion
        </button>

        <div className="pb-10"></div>
      </main>

      {/* Shared Floating Bottom Nav */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn" onClick={() => navigate('/my-points')}>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/rewards')}>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/scan-points')}>
          <div className="nav-center-scan">
            <QrCode size={24} color="white" />
          </div>
        </button>
        <button className="nav-btn" onClick={() => navigate('/wallet')}>
          <Wallet size={22} />
          <span>Wallet</span>
        </button>
        <button className="nav-btn is-active">
          <div className="nav-active-dot"></div>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default ClientProfile;